'use client';

import * as React from 'react';
import {
  CalendarCheck,
  CarFront,
  CheckCircle,
  Phone,
  ShieldCheck,
  Star
} from 'lucide-react';
import { motion } from 'motion/react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@workspace/ui/components/tabs';

import { BlurFade } from '~/components/fragments/blur-fade';
import { GridSection } from '~/components/fragments/grid-section';
import { TextGenerateEffect } from '~/components/fragments/text-generate-effect';

const rentalSteps = [
  {
    number: 1,
    title: 'Alegeți automobilul dorit',
    description:
      'Explorați selecția noastră exclusivistă de modele premium, atent alese pentru rafinament, tehnologie și performanță impecabilă.',
    icon: CarFront
  },
  {
    number: 2,
    title: 'Personalizați momentul preluării',
    description: 'Stabiliți cu ușurință data și ora ideale pentru preluare și predare, astfel încât experiența să se potrivească perfect ritmului dumneavoastră.',
    icon: CalendarCheck
  },
  {
    number: 3,
    title: 'Lăsați confortul să vă însoțească',
    description:
      'Ridicați automobilul și vă veți bucura de o călătorie fără efort, definită de lux, liniște și siguranță absolută.',
    icon: Star
  }
];

const rcaSteps = [
  {
    number: 1,
    title: 'Ați fost implicat într-un accident?',
    description:
      'Dacă ați fost parte dintr-un accident auto și nu ați fost vinovat, legea vă garantează dreptul la un vehicul de înlocuire, ale cărui costuri sunt acoperite de asigurarea RCA.',
    icon: ShieldCheck
  },
  {
    number: 2,
    title: 'Contactați-ne fără întârziere',
    description:
      'Fie că ne sunați sau completați formularul online, noi ne vom ocupa de toate demersurile necesare cu compania de asigurări.',
    icon: Phone
  },
  {
    number: 3,
    title: 'Beneficiați de un autoturism de lux',
    description:
      'Vă vom livra mașina premium decontată prin RCA direct la locația specificată de dumneavoastră și vă vom oferi toate informațiile relevante privind perioada de utilizare.',
    icon: CheckCircle
  }
];

function StepsGrid({
  steps
}: {
  steps: typeof rentalSteps;
}): React.JSX.Element {
  return (
    <div className="grid gap-16 sm:grid-cols-1 md:grid-cols-3">
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <BlurFade
            key={step.number}
            inView
            delay={0.1 + index * 0.1}
          >
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary/10 text-primary mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <Icon className="h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {step.number}. {step.title}
              </h3>
              <p className="text-muted-foreground">{step.description}</p>
            </div>
          </BlurFade>
        );
      })}
    </div>
  );
}

export function HowItWorks(): React.JSX.Element {
  return (
    <GridSection>
      <div className="bg-background container flex flex-col px-4 py-20 md:px-6">
        <div className="mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-semibold tracking-tight md:text-5xl">
              <TextGenerateEffect words="Soluții inteligente, simple și rapide" />
            </h2>
            <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-base">
              În orice moment, ai la dispoziție soluții adaptate pentru mobilitatea ta.
            </p>
          </motion.div>
        </div>

        <Tabs
          defaultValue="rental"
          className="w-full"
        >
          <TabsList className="mx-auto mb-12 grid w-fit grid-cols-2">
            <TabsTrigger
              value="rental"
              className="text-sm"
            >
              Experiențe de închiriere premium 
            </TabsTrigger>
            <TabsTrigger
              value="rca"
              className="text-sm"
            >
              Vehicul pus la dispoziție prin asigurare RCA
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="rental"
            className="mt-0"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-xl font-semibold">Închiriere de lux</h3>
              <p className="text-muted-foreground">
                Accesul la un automobil de elită nu a fost niciodată mai fluid și mai plăcut
              </p>
            </div>
            <StepsGrid steps={rentalSteps} />
          </TabsContent>

          <TabsContent
            value="rca"
            className="mt-0"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-xl font-semibold">
                Mașină acoperită de asigurarea RCA
              </h3>
              <p className="text-muted-foreground">
                Aveți dreptul legal de a beneficia de un autoturism premium de înlocuire
              </p>
            </div>
            <StepsGrid steps={rcaSteps} />
          </TabsContent>
        </Tabs>
      </div>
    </GridSection>
  );
}
